import {
    defineComponent,
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    nextTick,
    inject,
    reactive
} from "/Scripts/vue.esm-browser.js";
import { getTextWithoutSign } from "/Scripts/tools.js";
import { scrollService } from "/Services/Shop/scrollService.js";

const html = String.raw;
// === 等待 .topic_discount 出現 & 穩定 ===
export const runAfterTopicReady = async (opts = {}) => {
    const {
        selector = ".topic_discount", // 你要等的元素
        timeout = 12000, // 最長等待時間
        stableForMs = 180, // 尺寸在這段時間內不變才算穩定
        waitImages = true, // 是否等元素裡的圖片載完
    } = opts;

    if (typeof window === "undefined" || typeof document === "undefined") return;

    let el = null;
    try {
        el = await waitForEl(selector, timeout);
    } catch {
        /* 超時就略過 */
    }

    if (el && waitImages) await waitAllImages(el);
    if (el) await waitStable(el, stableForMs);

    await nextTick(); // 確保本輪 DOM 更新完成
};
const waitForEl = (selector, timeout = 10000) =>
    new Promise((resolve, reject) => {
        const exist = document.querySelector(selector);
        if (exist) return resolve(exist);

        const mo = new MutationObserver(() => {
            const el = document.querySelector(selector);
            if (el) {
                cleanup();
                resolve(el);
            }
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });

        const tid = setTimeout(() => {
            cleanup();
            reject(new Error("timeout"));
        }, timeout);
        function cleanup() {
            mo.disconnect();
            clearTimeout(tid);
        }
    });

const waitAllImages = (root) => {
    const imgs = Array.from(root.querySelectorAll("img")).filter(
        (img) => !img.complete || img.naturalWidth === 0,
    );
    if (!imgs.length) return Promise.resolve();
    return Promise.all(
        imgs.map(
            (img) =>
                new Promise((res) => {
                    img.addEventListener("load", () => res(), { once: true });
                    img.addEventListener("error", () => res(), { once: true });
                }),
        ),
    ).then(() => undefined);
};

const waitStable = (el, stableForMs = 150) =>
    new Promise((resolve) => {
        if (typeof ResizeObserver === "undefined") {
            setTimeout(resolve, stableForMs);
            return;
        }
        let t = null;
        const ro = new ResizeObserver(() => {
            if (t) clearTimeout(t);
            t = setTimeout(done, stableForMs);
        });
        function done() {
            if (t) {
                clearTimeout(t);
                t = null;
            }
            ro.disconnect();
            resolve();
        }
        ro.observe(el);
        t = setTimeout(done, stableForMs); // 初始保底
    });

export default defineComponent({
    name: "DestinationTopNav",
    props: {
        filteredDataByGroup: {
            type: Array,
            required: true,
        },
        sarea: {
            type: String,
            default: "",
        },
        oarea: {
            type: String,
            default: "",
        },
        beforeSarea: {
            type: String,
            default: "",
        },
    },
    emits: [],
    inject: ["groupShowType"],
    setup(props, { emit }) {
        const groupShowType = inject("groupShowType");
        const isFilterFinish = inject("isFilterFinish");
        const isScrollFinish = inject("isScrollFinish");
        const secondaryOpenSet = reactive(new Set());

        const first = ref(true); //只有第一次要前往目的地
        const filteredDataByGroup = computed(() => props.filteredDataByGroup);
        const selectedIndex = ref(-1);
        const selectedLocationIndex = ref(-1);
        const selectedThemeIndex = ref(-1);

        // 是否為單一區域，前台顯示效果不同
        const isSingleArea = computed(() => filteredDataByGroup.value.length === 1);
        const areas = computed(() => {
            const mappedAreas = filteredDataByGroup.value.map((item) => ({
                key: item.areaName,
                name: item.areaName,
                subArea: item.items?.[0]?.subArea ?? "",
                areas: (item.subAreas ?? []).map((subArea) => ({
                    key: `${item.areaName}|${subArea.subArea}`,
                    name: subArea.subArea,
                    subArea: subArea.items?.[0]?.subArea ?? subArea.subArea,
                    areas: (subArea.otherAreas ?? []).map((otherArea) => ({
                        key: `${subArea.subArea}|${otherArea.otherArea || otherArea.otherAreaName || ""}`,
                        name: otherArea.otherAreaName,
                        otherArea: otherArea.otherArea || "",
                    })),
                })),
            }));

            return mappedAreas.length === 1 ? mappedAreas[0]?.areas ?? [] : mappedAreas;
        });
        const selectedArea = computed(
            () => areas.value[(selectedIndex.value == -1 ? 0 : selectedIndex.value)] || {},
        );
        const selectedAreaLocations = computed(() =>
            isSingleArea.value ? [] : selectedArea.value?.areas || [],
        );
        const selectedAreaThemes = computed(() =>
            isSingleArea.value ? selectedArea.value?.areas || [] : [],
        );

        // 使用統一的選擇器對象進行元素查找
        const selectors = {
            area: {
                attr: () =>
                    isSingleArea.value ? "data-location-name" : "data-area-name",
                getTarget: (idx) => areas.value?.[idx]?.name,
            },
            location: {
                attr: "data-location-name",
                getTarget: (idx) => selectedArea.value?.areas?.[idx]?.name,
            },
            theme: {
                attr: "data-theme-name",
                getTarget: (idx, currentLocation) =>
                    getTextWithoutSign(currentLocation?.areas?.[idx]?.name),
            },
        };

        // 設置區域索引
        const setIndex = (idx, isClick = false) => {
            const targetElement = document.querySelector(
                `.topnav_region_primary .anchor_list>li:nth-child(${idx + 1})`,
            );
            if (targetElement) {
                setTimeout(() => {
                    targetElement.scrollIntoView({
                        inline: "center",
                        block: "nearest",
                        behavior: "smooth",
                    });
                }, 300);

            }

            let padding = 0;
            if (idx == 0) {
                const filterTagBlock = document.querySelector(".filterTag_block");
                padding = filterTagBlock.offsetHeight + 20;
            }
            scrollService.setIndexWithScroll(selectedIndex, idx, isClick, {
                type: "area",
                params: {
                    target: selectors.area.getTarget(idx),
                    attr: selectors.area.attr(),
                },
                scrollOptions: { padding },
            });
        };

        // 設置位置索引
        const setSelectedLocationIndex = (idx, isClick = false) => {
            if (!Number.isInteger(idx) || idx < 0) return;
            const targetElement = document.querySelector(
                `.anchor_list_region_secondary .anchor_list_container:nth-of-type(${idx + 1})`
            );
            if (targetElement) {
                setTimeout(() => {
                    targetElement.scrollIntoView({
                        inline: "center",
                        block: "nearest",
                        behavior: "smooth",
                    });
                }, 300);

            }
            scrollService.setIndexWithScroll(selectedLocationIndex, idx, isClick, {
                type: "location",
                params: {
                    target: selectors.location.getTarget(idx),
                },
            });
        };

        // 優化：設置主題索引
        const setSelectedThemeIndex = (idx, isClick = false) => {
            if (!Number.isInteger(idx) || idx < 0) return;
            if (isSingleArea.value) {
                const targetElement = document.querySelector(
                    `.anchor_list_region_secondary .anchor_list > li:nth-child(${idx + 1})`
                );
                if (targetElement) {
                    setTimeout(() => {
                        targetElement.scrollIntoView({
                            inline: "center",
                            block: "nearest",
                            behavior: "smooth",
                        });
                    }, 300);

                }
            }
            else {
                if (!Number.isInteger(selectedLocationIndex.value) || selectedLocationIndex.value < 0) {
                    return;
                }
                const targetElement = document.querySelector(
                    `.anchor_list_region_secondary .anchor_list_container:nth-of-type(${selectedLocationIndex.value + 1}) .anchor_list > li:nth-child(${idx + 1})`
                );
                if (targetElement) {
                    setTimeout(() => {
                        targetElement.scrollIntoView({
                            inline: "center",
                            block: "nearest",
                            behavior: "smooth",
                        });
                    }, 300);

                }
            }


            const currentLocation = isSingleArea.value
                ? selectedArea.value
                : selectedArea.value?.areas?.[selectedLocationIndex.value];

            if (!currentLocation) return;

            scrollService.setIndexWithScroll(selectedThemeIndex, idx, isClick, {
                type: "theme",
                params: {
                    target: selectors.theme.getTarget(idx, currentLocation),
                    currentLocation,
                },
            });
        };

        // 創建滾動處理函數
        const handleScroll = scrollService.createScrollHandler({
            areas,
            isSingleArea,
            setIndex,
            setSelectedLocationIndex,
            setSelectedThemeIndex,
            selectedArea,
            selectedLocationIndex,
        });

        const parseAreaName = (name) => {
            const match = name.match(/^(.+?)（(.+?)）$/); // 注意是全形括號
            if (match) {
                return {
                    main: match[1],
                    sub: match[2],
                };
            }
            return {
                main: name,
                sub: null,
            };
        };
        const setUrl = (sarea, oarea) => {
            const currentPath = window.location.pathname; // 例如 /destination/7
            const queryString = window.location.search; // 例如 ?weekdays=1

            // 使用正規表達式抓出 /destination/Sn 中的 Sn
            const match = currentPath.match(/^\/destination\/([^\/]+)/i);

            if (match) {
                const sn = match[1];
                let newPath = "";
                if (oarea) {
                    newPath = `/destination/${sn}/${sarea}/${oarea}${queryString}`;
                } else {
                    newPath = `/destination/${sn}/${sarea}${queryString}`;
                }

                window.history.pushState({}, "", newPath);
            } else {
                console.error("網址不符 /destination/Sn 格式");
            }
        };
        const findSelectedIndexes = (areas, sarea, oarea) => {
            for (let i = 0; i < areas.length; i++) {
                const country = areas[i];
                if (!country.areas) continue;

                for (let j = 0; j < country.areas.length; j++) {
                    const location = country.areas[j];
                    if (!location.areas) continue;

                    // ✅ 確認 location.subArea === sarea
                    if (location.subArea !== sarea) continue;

                    for (let k = 0; k < location.areas.length; k++) {
                        const theme = location.areas[k];

                        // ✅ 確認 theme.otherArea === oarea
                        if (theme.otherArea === oarea) {
                            return {
                                selectedIndex: i,
                                selectedLocationIndex: j,
                                selectedThemeIndex: k,
                            };
                        }
                    }
                }
            }

            return null; // 沒找到
        };
        const defaultScroll = async () => {
            // --- 只有小分類（sarea），沒有其他分類（oarea） ---
            if (props.sarea && !props.oarea) {
                // 單層結構：直接在 areas.value 找
                const idx = areas.value.findIndex((item) => item.subArea === props.sarea);
                if (idx > -1) {
                    await runAfterTopicReady({
                        selector: ".topic_discount",
                        timeout: 1500,
                        stableForMs: 180,
                    });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                    setIndex(idx, true);
                    first.value = false;
                    isScrollFinish.value = true;
                    return;
                }
                // 多層結構：往內層 areas 找
                for (let i = 0; i < areas.value.length; i++) {
                    const innerIdx = areas.value[i]?.areas?.findIndex(
                        (item) => item.subArea === props.sarea,
                    );
                    if (innerIdx > -1) {
                        await runAfterTopicReady({
                            selector: ".topic_discount",
                            timeout: 1500,
                            stableForMs: 180,
                        });
                        selectedIndex.value = i;
                        setSelectedLocationIndex(innerIdx, true);
                        first.value = false;
                        isScrollFinish.value = true;
                        return;
                    }
                }
                return;
            }

            // --- 同時有小分類（sarea）與其他分類（oarea） ---
            if (props.sarea && props.oarea) {
                const oareaKey = props.oarea === "精選行程" ? "" : props.oarea;

                if (isSingleArea.value) {
                    // 單個大分類
                    const group = areas.value.find((item) => item.subArea === props.sarea);
                    if (!group) return;

                    const idx = areas.value.findIndex(
                        (item) => item.subArea === props.sarea,
                    );
                    const subIdx = group?.areas?.findIndex(
                        (sub) => sub.otherArea === oareaKey,
                    );

                    if (subIdx > -1) {
                        await runAfterTopicReady({
                            selector: ".topic_discount",
                            timeout: 1500,
                            stableForMs: 180,
                        });
                        selectedIndex.value = idx;
                        selectedThemeIndex.value = subIdx;
                        setSelectedThemeIndex(subIdx, true);
                        first.value = false;
                        isScrollFinish.value = true;
                        return;
                    }
                } else {
                    // 多個大分類
                    const result = findSelectedIndexes(areas.value, props.sarea, oareaKey);
                    if (result) {
                        await runAfterTopicReady({
                            selector: ".topic_discount",
                            timeout: 1500,
                            stableForMs: 180,
                        });
                        selectedIndex.value = result.selectedIndex;
                        selectedLocationIndex.value = result.selectedLocationIndex;
                        setSelectedThemeIndex(result.selectedThemeIndex, true);
                        first.value = false;
                        isScrollFinish.value = true;
                        return;
                    }
                }
            }
        }

        const openSecondary = (idx) => {
            secondaryOpenSet.add(idx);     //滑鼠移過去就增加is-open
        }
        const clearSecondaryOpen = () => {
            secondaryOpenSet.clear();  //滑鼠離開就全部is-open都移除
        }

        const handleAreaClick = (idx, area) => {
            setIndex(idx, true);
            setUrl(area.subArea, "");
        };

        const handleLocationClick = (idx, area) => {
            setSelectedLocationIndex(idx, true);
            setUrl(area.subArea, "");
        };

        const handleThemeClick = (idx, area, subArea) => {
            if (area) {
                selectedLocationIndex.value = selectedAreaLocations.value.findIndex(
                    (item) => item.subArea === area.subArea,
                );
            }
            setSelectedThemeIndex(idx, true);
            setUrl(
                area?.subArea || selectedArea.value.subArea,
                subArea.otherArea || "精選行程",
            );
        };

        const handleViewModeClick = (nextGroupShowType) => {
            groupShowType.value = nextGroupShowType;
        };

        watch(
            isFilterFinish,
            async (newVal) => {
                if (newVal) {
                    defaultScroll();
                }
            },
            { immediate: true, flush: "post" },
        );

        watch(
            [areas, selectedAreaLocations, selectedAreaThemes, isSingleArea],
            ([newAreas, newLocations, newThemes, singleArea]) => {
                if (!newAreas?.length) return;

                if (selectedIndex.value < 0 || selectedIndex.value >= newAreas.length) {
                    selectedIndex.value = 0;
                }

                if (singleArea) {
                    if (newThemes?.length) {
                        if (
                            selectedThemeIndex.value < 0 ||
                            selectedThemeIndex.value >= newThemes.length
                        ) {
                            selectedThemeIndex.value = 0;
                        }
                    } else {
                        selectedThemeIndex.value = -1;
                    }
                    selectedLocationIndex.value = -1;
                    return;
                }

                if (newLocations?.length) {
                    if (
                        selectedLocationIndex.value < 0 ||
                        selectedLocationIndex.value >= newLocations.length
                    ) {
                        selectedLocationIndex.value = 0;
                    }

                    const currentThemes =
                        newLocations[selectedLocationIndex.value]?.themes || [];
                    if (currentThemes.length) {
                        if (
                            selectedThemeIndex.value < 0 ||
                            selectedThemeIndex.value >= currentThemes.length
                        ) {
                            selectedThemeIndex.value = 0;
                        }
                    } else {
                        selectedThemeIndex.value = -1;
                    }
                } else {
                    selectedLocationIndex.value = -1;
                    selectedThemeIndex.value = -1;
                }
            },
            { immediate: true },
        );
        //watch(
        //    areas,
        //    async (newVal) => {
        //        if (!newVal || newVal.length === 0) return; // 沒資料就返回
        //        if (!first.value) return; // 只在第一次生效
        //        if (!props.sarea && !props.oarea) return; // 沒帶參數就返回
        //        // ⭐ 這裡開始等 isFilterFinish 變成 true 才往下
        //        const start = Date.now();

        //        while (!isFilterFinish.value) {
        //            await new Promise((resolve) => setTimeout(resolve, 100));

        //            // 超過 3 秒就離開
        //            if (Date.now() - start > 3000) {
        //                //console.warn("等待 isFilterFinish 超過 3 秒，自動跳出");
        //                break;
        //            }
        //        }
        //        // --- 只有小分類（sarea），沒有其他分類（oarea） ---
        //        if (props.sarea && !props.oarea) {
        //            // 單層結構：直接在 newVal 找
        //            const idx = newVal.findIndex((item) => item.subArea === props.sarea);
        //            if (idx > -1) {
        //                await runAfterTopicReady({
        //                    selector: ".topic_discount",
        //                    timeout: 1500,
        //                    stableForMs: 180,
        //                });
        //                await new Promise((resolve) => setTimeout(resolve, 1000));
        //                setIndex(idx, true);
        //                first.value = false;
        //                isScrollFinish.value = true;
        //                return;
        //            }
        //            // 多層結構：往內層 areas 找
        //            for (let i = 0; i < newVal.length; i++) {
        //                const innerIdx = newVal[i]?.areas?.findIndex(
        //                    (item) => item.subArea === props.sarea,
        //                );
        //                if (innerIdx > -1) {
        //                    await runAfterTopicReady({
        //                        selector: ".topic_discount",
        //                        timeout: 1500,
        //                        stableForMs: 180,
        //                    });
        //                    selectedIndex.value = i;
        //                    setSelectedLocationIndex(innerIdx, true);
        //                    first.value = false;
        //                    isScrollFinish.value = true;
        //                    return;
        //                }
        //            }
        //            return;
        //        }

        //        // --- 同時有小分類（sarea）與其他分類（oarea） ---
        //        if (props.sarea && props.oarea) {
        //            const oareaKey = props.oarea === "精選行程" ? "" : props.oarea;

        //            if (isSingleArea.value) {
        //                // 單個大分類
        //                const group = newVal.find((item) => item.subArea === props.sarea);
        //                if (!group) return;

        //                const idx = newVal.findIndex(
        //                    (item) => item.subArea === props.sarea,
        //                );
        //                const subIdx = group?.areas?.findIndex(
        //                    (sub) => sub.otherArea === oareaKey,
        //                );

        //                if (subIdx > -1) {
        //                    await runAfterTopicReady({
        //                        selector: ".topic_discount",
        //                        timeout: 1500,
        //                        stableForMs: 180,
        //                    });
        //                    selectedIndex.value = idx;
        //                    selectedThemeIndex.value = subIdx;
        //                    setSelectedThemeIndex(subIdx, true);
        //                    first.value = false;
        //                    isScrollFinish.value = true;
        //                    return;
        //                }
        //            } else {
        //                // 多個大分類
        //                const result = findSelectedIndexes(newVal, props.sarea, oareaKey);
        //                if (result) {
        //                    await runAfterTopicReady({
        //                        selector: ".topic_discount",
        //                        timeout: 1500,
        //                        stableForMs: 180,
        //                    });
        //                    selectedIndex.value = result.selectedIndex;
        //                    selectedLocationIndex.value = result.selectedLocationIndex;
        //                    setSelectedThemeIndex(result.selectedThemeIndex, true);
        //                    first.value = false;
        //                    isScrollFinish.value = true;
        //                    return;
        //                }
        //            }
        //        }
        //    },
        //    { immediate: true, flush: "post" },
        //);
        let observer;

        onMounted(() => {
            // 使用 passive: true 優化滾動效能
            window.addEventListener("scroll", handleScroll, { passive: true });
            if (props.sarea) {
                const index = areas.value.findIndex(
                    (item) => item.subArea === props.sarea,
                );
                if (index > -1) {
                    setIndex(index, true);
                }
            }

            setTimeout(() => {
                const topicWrap = document.querySelector(".topic_wrap");
                //console.log("抓到 topic_wrap:", topicWrap);

                if (!topicWrap) {
                    //console.warn("⚠️ topic_wrap 不存在");
                    return;
                }

                observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            selectedIndex.value = -1;
                            selectedLocationIndex.value = -1;
                            selectedThemeIndex.value = -1;
                        } else {
                            //console.log("❌ .topic_wrap 離開畫面");
                        }
                    });
                });

                observer.observe(topicWrap);
            }, 2000); // 延遲 500ms 再綁
        });

        onUnmounted(() => {
            // 清理事件監聽器
            window.removeEventListener("scroll", handleScroll);

            if (observer) {
                observer.disconnect();
            }
        });

        return {
            filteredDataByGroup,
            areas,
            selectedIndex,
            setIndex,
            selectedArea,
            selectedAreaLocations,
            selectedAreaThemes,
            selectedLocationIndex,
            setSelectedLocationIndex,
            selectedThemeIndex,
            setSelectedThemeIndex,
            isSingleArea,
            parseAreaName,
            setUrl,
            openSecondary,
            clearSecondaryOpen,
            secondaryOpenSet,
            handleAreaClick,
            handleLocationClick,
            handleThemeClick,
            handleViewModeClick
        };
    },
    mounted() {
        // 置頂導覽列 顯示切換
        const TopNav = document.querySelector(".destination_topnav");

        if (TopNav) {
            const destination_topnav = scrollService.createTopNavVisibilityHandler();

            // 監聽滾動事件
            window.addEventListener("scroll", destination_topnav);

            // DOM 載入完成後執行
            document.addEventListener("DOMContentLoaded", destination_topnav);

            // 在 mounted 時先執行一次
            destination_topnav();

            // 監聽大小變化
            const topnavObserver = new ResizeObserver((entries) => {
                // 確保 DOM 完全載入後執行
                if (
                    document.readyState === "complete" ||
                    document.readyState === "interactive"
                ) {
                    const primaryRegion = document.querySelector(
                        ".topnav_region_primary",
                    );
                    const secondaryRegion = document.querySelector(
                        ".anchor_list_region_secondary",
                    );

                    if (primaryRegion) scrollService.handleNavOverflow(primaryRegion);
                    if (secondaryRegion) scrollService.handleNavOverflow(secondaryRegion);
                } else {
                    document.addEventListener("DOMContentLoaded", () => {
                        const primaryRegion = document.querySelector(
                            ".topnav_region_primary",
                        );
                        const secondaryRegion = document.querySelector(
                            ".anchor_list_region_secondary",
                        );

                        if (primaryRegion) scrollService.handleNavOverflow(primaryRegion);
                        if (secondaryRegion)
                            scrollService.handleNavOverflow(secondaryRegion);
                    });
                }
            });

            // 觀察主要和次要導覽區域的變化
            const primaryRegion = document.querySelector(".topnav_region_primary");
            const secondaryRegion = document.querySelector(
                ".anchor_list_region_secondary",
            );

            if (primaryRegion) topnavObserver.observe(primaryRegion);
            if (secondaryRegion) topnavObserver.observe(secondaryRegion);

            // 初始執行一次
            const initialPrimaryRegion = document.querySelector(
                ".topnav_region_primary",
            );
            const initialSecondaryRegion = document.querySelector(
                ".anchor_list_region_secondary",
            );

            if (initialPrimaryRegion)
                scrollService.handleNavOverflow(initialPrimaryRegion);
            if (initialSecondaryRegion)
                scrollService.handleNavOverflow(initialSecondaryRegion);
        }
    },

    template: html`
    <div class="destination_topnav">
      <div class="topnav_region_primary">
        <div class="anchor_list_region">
          <div class="anchor_list_region_container">
            <div class="anchor_list_wrap horizontal dragscroll">
              <div class="anchor_list_container">
                <ul class="anchor_list">
                  <li
                    class="anchor_btn"
                    v-for="(area, idx) in areas"
                    :key="area.key || area.name"
                    :class="{ active: idx === selectedIndex }"
                    @click="handleAreaClick(idx, area)"
                  >
                    <a href="javascript:void(0)">
                      {{ parseAreaName(area.name).main }}
                      <span v-if="parseAreaName(area.name).sub">
                        {{ parseAreaName(area.name).sub }}
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
              <div class="overflow_more-start"></div>
              <div class="overflow_more-end"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="topnav_region_secondary horizontal dragscroll">
        <div class="anchor_list_region_secondary">
          <div class="anchor_list_wrap horizontal dragscroll" @mouseleave="clearSecondaryOpen()">
            <div
              v-if="filteredDataByGroup.length > 1"
              class="anchor_list_container"
              v-for="(area, idx) in selectedAreaLocations"
              :key="area.key || area.name"
              :class="{ 'is-open': secondaryOpenSet.has(idx) }"
              @mouseenter="openSecondary(idx)"
              @click="handleLocationClick(idx, area)"
            >
              <h3 :class="{ active: idx === selectedLocationIndex }">
                <a href="javascript:void(0)">{{ area.name }}</a>
              </h3>
              <ul class="anchor_list">
                <li
                  class="anchor_btn"
                  v-for="(subArea, sidx) in area.areas.filter(item=>item.name)"
                  :key="subArea.key || subArea.name"
                  :class="{ active: sidx === selectedThemeIndex }"
                  @click.stop="handleThemeClick(sidx, area, subArea)"
                >
                  <a href="javascript:void(0)"> {{ subArea.name}} </a>
                </li>
              </ul>
            </div>
            <div v-else class="anchor_list_container">
              <ul class="anchor_list">
                <li
                  class="anchor_btn"
                  v-for="(subArea, idx) in selectedAreaThemes"
                  :key="subArea.key || subArea.name"
                  :class="{ active: idx === selectedThemeIndex }"
                  @click="handleThemeClick(idx, null, subArea)"
                >
                  <a href="javascript:void(0)">
                    {{ subArea.name || "精選行程" }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="view_btn_box">
            <button
              class="view_btn view_btn-list tab"
              :class="{ active: groupShowType === 'horizontal' }"
              data-layout="horizontal"
              @click="handleViewModeClick('horizontal')"
            >
              <img src="/Content/Img/Shop/icon_list-black.png" alt="" />
              條列
            </button>
            <button
              class="view_btn view_btn-img tab vertical"
              :class="{ active: groupShowType === 'vertical' }"
              data-layout="vertical"
              @click="handleViewModeClick('vertical')"
            >
              <img src="/Content/Img/Shop/icon_img-black.png" alt="" />
              圖卡
            </button>
            <button
              class="view_btn view_btn-date tab"
              :class="{ active: groupShowType === 'uni' }"
              data-layout="uni"
              @click="handleViewModeClick('uni')"
            >
              <img src="/Content/Img/Shop/icon_calendar-black.png" alt="" />
              日期
            </button>
          </div>
          <div class="overflow_more-start"></div>
          <div class="overflow_more-end"></div>
        </div>
      </div>
    </div>
  `,
    methods: {},
});
